/** Split a camelCase, snake_case, or kebab-case string into words. */
export const splitWords = (s: string): string[] =>
  s.split(/(?=[A-Z])|[_-]/).filter(Boolean);

/** Sentence-case a list of words: capitalise the first, lowercase the rest. */
export const sentenceCase = (words: string[]): string =>
  capitalize(words.join(' ').toLowerCase());

export const capitalize = (val: string) =>
  val.charAt(0).toUpperCase() + val.slice(1);

/** The words in a text: each run of letters, digits or apostrophes. */
export const words = (text: string): string[] =>
  text.match(/[\p{L}\p{N}']+/gu) ?? [];

/** Trims each part and drops the blank ones. */
export const nonBlank = (parts: string[]): string[] =>
  parts.map((part) => part.trim()).filter(Boolean);

/** Joins the parts that are not blank, a line each. */
export const joinLines = (parts: string[]): string =>
  nonBlank(parts).join('\n');

/**
 * Generate a v4 UUID. Falls back to `crypto.getRandomValues` where
 * `crypto.randomUUID` is unavailable (iOS < 15.4, non-secure contexts).
 */
export const randomUUID = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4),
    hex.slice(4, 6),
    hex.slice(6, 8),
    hex.slice(8, 10),
    hex.slice(10, 16),
  ]
    .map((part) => part.join(''))
    .join('-');
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a string is a UUID, of any version, such as an ID from a URL. */
export const isUUID = (val: string): boolean => UUID.test(val);
